import { resolveThemeAiImageStyle } from "common/utils/themeAiSettings";

/**
 * Reads a theme attribute from either a loaded Theme model or a plain attributes
 * object — the theme editor works on plain attributes while the rest of the app
 * holds Backbone-backed models.
 */
export function readThemeAttribute(theme, key) {
    if (!theme) {
        return undefined;
    }

    const value = typeof theme.get === "function" ? theme.get(key) : theme[key];
    return value ?? undefined;
}

/** The AI settings a kit carries, empty when it has none. */
export function getThemeAiRules(theme) {
    return readThemeAttribute(theme, "aiRules") ?? {};
}

/** The brand voice a kit defines, for surfaces that rewrite existing copy. */
export function getThemeBrandVoice(theme) {
    return getThemeAiRules(theme).voiceAndTone ?? undefined;
}

/**
 * The AI generation defaults a brand kit contributes.
 *
 * These seed the generation UI so that what the user is about to generate visibly
 * matches the kit; they are defaults, not constraints, and any of them can still
 * be overridden for a single generation. Fields the kit doesn't set are left
 * undefined so callers can fall through to their own fallbacks.
 */
export function getThemeAiDefaults(theme) {
    const { imageSource, language } = getThemeAiRules(theme);

    return {
        imageSource: imageSource ?? undefined,
        imageStyle: resolveThemeAiImageStyle({
            imageStyle: readThemeAttribute(theme, "imageStyle"),
            imageStylePrompt: readThemeAttribute(theme, "imageStylePrompt")
        }),
        language: language ?? undefined
    };
}

/**
 * Layers a brand kit's AI defaults under the values already in hand.
 *
 * Image choices are only taken from the kit when `adoptImageDefaults` is set —
 * the caller passes false once the user has generated against this kit before, so
 * their last-used image source and style aren't silently reset. Not adopting only
 * demotes the kit below the current value; it still fills a gap.
 */
export function mergeThemeAiDefaults(current, themeDefaults, { adoptImageDefaults = true } = {}) {
    const imageSource = adoptImageDefaults
        ? themeDefaults.imageSource ?? current.imageSource
        : current.imageSource ?? themeDefaults.imageSource;
    const imageStyle = adoptImageDefaults
        ? themeDefaults.imageStyle ?? current.imageStyle
        : current.imageStyle ?? themeDefaults.imageStyle;

    return {
        imageSource,
        imageStyle,
        language: themeDefaults.language ?? current.language
    };
}
